<div class="community-profile-section" *transloco="let t">
  @if (loading()) {
    <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
  } @else if (error(); as key) {
    <div class="alert alert-danger d-flex justify-content-between align-items-start">
      <span>{{ t(key) }}</span>
      <button type="button" class="btn-close" (click)="error.set(null)"></button>
    </div>
  } @else if (profile(); as p) {
    @if (saved()) {
      <div class="alert alert-success py-2">{{ t('community.profile.saved') }}</div>
    }

    <!-- Avatar -------------------------------------------------------------- -->
    <div class="d-flex align-items-center gap-3 mb-4">
      <app-community-avatar [user]="p.user" [size]="80" />
      <div>
        @if (avatarError(); as key) {
          <div class="text-danger small mb-1">{{ t(key) }}</div>
        }
        <label class="btn btn-sm btn-outline-secondary mb-0">
          {{ t('community.profile.avatar_upload') }}
          <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden [disabled]="avatarBusy()" (change)="onAvatarSelected($event)" />
        </label>
        @if (p.user.avatar_url) {
          <button type="button" class="btn btn-sm btn-outline-danger ms-2" [disabled]="avatarBusy()" (click)="removeAvatar()">
            {{ t('community.profile.avatar_remove') }}
          </button>
        }
        <div class="form-text">{{ t('community.profile.avatar_hint') }}</div>
      </div>
    </div>

    <!-- Bio ------------------------------------------------------------------ -->
    <div class="mb-3">
      <label class="form-label">{{ t('community.profile.bio') }}</label>
      <textarea class="form-control" rows="4" [maxLength]="p.limits.bio" [(ngModel)]="bio"></textarea>
      <div class="form-text text-end">{{ bio.length }} / {{ p.limits.bio }}</div>
    </div>

    <!-- About you: hobbies, interests, topics — "profiles connect people" ---------- -->
    <div class="card mb-3">
      <div class="card-header fw-semibold">{{ t('community.profile.people_title') }}</div>
      <div class="card-body">
        <p class="text-muted small">{{ t('community.profile.people_hint') }}</p>
        @for (field of listFields(); track field) {
          <div class="row g-2 align-items-start mb-2 community-profile-section__field">
            <div class="col-sm-8">
              <label class="form-label small mb-0">{{ t('community.profile.' + field) }}</label>
              <input type="text" class="form-control form-control-sm"
                     [ngModel]="listValue(field)" (ngModelChange)="setListValue(field, $event)"
                     [placeholder]="t('community.profile.list_hint', { max: p.limits.list_items ?? 20 })" />
            </div>
            <div class="col-sm-4">
              <select class="form-select form-select-sm" [ngModel]="visibilityOf(field)" (ngModelChange)="setVisibility(field, $event)">
                @for (v of visibilities; track v) {
                  <option [value]="v">{{ t('community.profile.visibility.' + v) }}</option>
                }
              </select>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Where you are: country + region, the member's choice, never the address ----- -->
    <div class="card mb-3">
      <div class="card-header fw-semibold">{{ t('community.profile.location_title') }}</div>
      <div class="card-body">
        <p class="text-muted small">{{ t('community.profile.location_hint') }}</p>
        <div class="row g-2 align-items-start mb-2 community-profile-section__field">
          <div class="col-sm-8">
            <label class="form-label small mb-0">{{ t('community.profile.fields.country') }}</label>
            <input type="text" class="form-control form-control-sm" maxlength="3" [(ngModel)]="countryCode" placeholder="US" />
            @if (!countryCode && p.suggested?.country_code) {
              <button type="button" class="btn btn-link btn-sm p-0" (click)="countryCode = p.suggested!.country_code!">
                {{ t('community.profile.use_suggested', { value: p.suggested!.country_code }) }}
              </button>
            }
          </div>
          <div class="col-sm-4">
            <select class="form-select form-select-sm" [ngModel]="visibilityOf('country')" (ngModelChange)="setVisibility('country', $event)">
              @for (v of visibilities; track v) {
                <option [value]="v">{{ t('community.profile.visibility.' + v) }}</option>
              }
            </select>
          </div>
        </div>
        <div class="row g-2 align-items-start mb-2 community-profile-section__field">
          <div class="col-sm-8">
            <label class="form-label small mb-0">{{ t('community.profile.fields.region') }}</label>
            <input type="text" class="form-control form-control-sm" maxlength="100" [(ngModel)]="region" placeholder="Florida" />
            @if (!region && p.suggested?.region) {
              <button type="button" class="btn btn-link btn-sm p-0" (click)="region = p.suggested!.region!">
                {{ t('community.profile.use_suggested', { value: p.suggested!.region }) }}
              </button>
            }
          </div>
          <div class="col-sm-4">
            <select class="form-select form-select-sm" [ngModel]="visibilityOf('region')" (ngModelChange)="setVisibility('region', $event)">
              @for (v of visibilities; track v) {
                <option [value]="v">{{ t('community.profile.visibility.' + v) }}</option>
              }
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Practitioner ----------------------------------------------------------- -->
    @if (p.is_practitioner) {
      <div class="card mb-3">
        <div class="card-header fw-semibold">{{ t('community.member.practitioner') }}</div>
        <div class="card-body">
          <div class="mb-3">
            <label class="form-label">{{ t('community.profile.calling') }}</label>
            <textarea class="form-control" rows="3" [maxLength]="p.limits.calling" [(ngModel)]="calling"></textarea>
            <div class="form-text text-end">{{ calling.length }} / {{ p.limits.calling }}</div>
          </div>
          <!-- The professional block (specialty … languages); keys come from the backend. -->
          <p class="text-muted small mb-2">{{ t('community.profile.professional_hint') }}</p>
          @for (key of detailKeys(); track key) {
            <div class="mb-2">
              <label class="form-label small mb-0">{{ t('community.profile.details.' + key) }}</label>
              <input type="text" class="form-control form-control-sm" [maxLength]="p.limits.detail ?? 500"
                     [ngModel]="detailValue(key)" (ngModelChange)="setDetailValue(key, $event)" />
            </div>
          }
          <div class="form-check mb-2 mt-3">
            <input class="form-check-input" type="checkbox" id="practitionerVisible" [(ngModel)]="practitionerVisible" />
            <label class="form-check-label" for="practitionerVisible">{{ t('community.profile.practitioner_visible') }}</label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="practitionerAcceptsDm" [(ngModel)]="practitionerAcceptsDm" [disabled]="!practitionerVisible" />
            <label class="form-check-label" for="practitionerAcceptsDm">{{ t('community.profile.practitioner_accepts_dm') }}</label>
          </div>
        </div>
      </div>
    }

    <!-- Contact fields ---------------------------------------------------------- -->
    <div class="card mb-3">
      <div class="card-header fw-semibold">{{ t('community.member.contact') }}</div>
      <div class="card-body">
        <p class="text-muted small">{{ t('community.profile.visibility_hint') }}</p>

        @for (field of p.contact_fields; track field) {
          @if (field !== 'social' && field !== 'country') {
            <div class="row g-2 align-items-center mb-2 community-profile-section__field">
              <div class="col-sm-4">
                <label class="form-label small mb-0">{{ t('community.profile.fields.' + field) }}</label>
                @if (editableFields.has(field)) {
                  <input type="text" class="form-control form-control-sm"
                         [ngModel]="editableValue(field)" (ngModelChange)="setEditableValue(field, $event)" />
                } @else {
                  <div class="form-control-plaintext form-control-sm py-0">{{ fieldValue(field) || '—' }}</div>
                }
              </div>
              <div class="col-sm-4">
                <select class="form-select form-select-sm" [ngModel]="visibilityOf(field)" (ngModelChange)="setVisibility(field, $event)">
                  @for (v of visibilities; track v) {
                    <option [value]="v">{{ t('community.profile.visibility.' + v) }}</option>
                  }
                </select>
              </div>
            </div>
          }
        }

        @if (p.contact_fields.includes('social')) {
          <hr />
          <div class="row g-2 align-items-start mb-2">
            <div class="col-sm-4">
              <label class="form-label small mb-0">{{ t('community.profile.fields.social') }}</label>
              @for (key of p.social_keys; track key) {
                <div class="input-group input-group-sm mb-1">
                  <span class="input-group-text community-profile-section__social-key">{{ t('community.profile.social.' + key) }}</span>
                  <input type="text" class="form-control" [ngModel]="socialValue(key)" (ngModelChange)="setSocialValue(key, $event)" />
                </div>
              }
            </div>
            <div class="col-sm-4">
              <select class="form-select form-select-sm" [ngModel]="visibilityOf('social')" (ngModelChange)="setVisibility('social', $event)">
                @for (v of visibilities; track v) {
                  <option [value]="v">{{ t('community.profile.visibility.' + v) }}</option>
                }
              </select>
            </div>
          </div>
        }
      </div>
    </div>

    <div class="d-flex justify-content-end mb-4">
      <button type="button" class="btn btn-primary" [disabled]="saving()" (click)="save()">
        @if (saving()) { <span class="spinner-border spinner-border-sm me-1" role="status"></span> }
        {{ t('community.profile.save') }}
      </button>
    </div>

    <!-- Blocked members --------------------------------------------------------- -->
    <div class="card">
      <div class="card-header fw-semibold">{{ t('community.profile.blocked_title') }}</div>
      <div class="card-body">
        @if (blockedLoading()) {
          <div class="text-center py-2"><div class="spinner-border spinner-border-sm" role="status"></div></div>
        } @else if (blocked().length === 0) {
          <p class="text-muted small mb-0">{{ t('community.profile.blocked_empty') }}</p>
        } @else {
          <ul class="list-group">
            @for (u of blocked(); track u.id) {
              <li class="list-group-item d-flex align-items-center gap-2">
                <app-community-avatar [user]="u" [size]="28" />
                <a [routerLink]="memberLink(u.id)" class="flex-grow-1">{{ u.full_name || u.username || ('#' + u.id) }}</a>
                <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="blockBusy() === u.id" (click)="unblock(u)">
                  {{ t('community.member.unblock') }}
                </button>
              </li>
            }
          </ul>
        }
      </div>
    </div>
  }
</div>
